import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
  applyBrandToCard,
  type BrandKit,
  isBrandConfigured,
  brandPalette,
  brandBackgroundFor,
  allowedImageMimeTypes,
  bookendFontCategories,
  cardLabel,
  bookendFonts,
  bookendTextDefaults,
  backgroundPatternLabels,
  backgroundPatterns,
  cardAnchors,
  cardContent,
  anchoredBoxes,
  applyCardPreset,
  cardMotionTypes,
  cardPresets,
  cardShapes,
  findCardPreset,
  cardShadowPresets,
  matchShadowPreset,
  type CardShadowPreset,
  newCardImage,
  newCardShape,
  defaultFrame,
  findBookendFont,
  newCardText,
  focusModes,
  nearestWeight,
  primaryFamily,
  uploadedFontStack,
  standardEasing,
  transitionPaces,
  type Background,
  type CardElement,
  type CardMotion,
  type CardMotionType,
  type CardPresetRef,
  type CardShape,
  type ProjectFont,
  type FocusEffect,
  type BookendScene,
  type Rect,
  type TextStyle,
  type Timeline,
  type Transition,
  backgroundColours,
  meshLayouts,
  meshLayoutLabels,
  paletteFamilies,
  paletteBackground,
  paletteBehind,
  palettes,
  type Palette,
  type PaletteFamily,
} from "@veyso/core";
import { applyBookendVoiceOffset, setOriginalAudio } from "@veyso/timeline";
import { ColorField, Eyebrow, Field, Select, Slider, Toggle, timecode } from "../components/ui.js";
import { MeshLayer, PatternLayer } from "@veyso/remotion";
import { RewritePane, type RewritePaneProps } from "./RewritePane.js";
import { TransitionPicker, cardFace, recordingFace, type BoundaryFaces } from "./TransitionPicker.js";
import type { Filmstrip } from "./SyncPoint.js";
import { api } from "../api.js";

export type Pane = "HEAD" | "BODY" | "END" | "TRANSITIONS" | "CAPTIONS" | "ACTIONS" | "REWRITE";

/**
 * What the properties header calls each pane.
 *
 * The key doubles as the label everywhere else, which is fine while the two
 * agree. "REWRITE" does not agree with the button that opens it, and a panel
 * titled differently from the control you just pressed reads as a different
 * panel.
 */
export const paneLabel = (pane: Pane): string => (pane === "REWRITE" ? "AI REWRITE" : pane);

export interface InspectorProps {
  projectId: string;
  timeline: Timeline;
  pane: Pane;
  commit: (mutate: (draft: Timeline) => Timeline) => void;
  selectedActionId: string | null;
  onSelectAction: (id: string | null) => void;
  /** The effect being edited. An effect need not belong to an action. */
  selectedFocusId: string | null;
  onSelectFocus: (id: string | null) => void;
  /** The card element being edited, shared with the preview so one box is selected in both. */
  selectedElementId: string | null;
  onSelectElement: (id: string | null) => void;
  /** Where a new effect is dropped, in body seconds. */
  playheadSeconds: number;
  onSeekSeconds: (seconds: number) => void;
  /** Called once the server has rebuilt the timeline. */
  onRebuilt: () => void;
  /** The master recording's own dimensions, which the timeline does not carry. */
  recording?: { durationSeconds: number | null; width: number | null; height: number | null };
  /** Passed straight to the rewrite pane, which is where the cut is undone. */
  autoCut?: RewritePaneProps["autoCut"];
  /** The recording's contact sheet, so the transition tiles preview real frames. */
  filmstrip?: Filmstrip | null;
}

/**
 * Ready-made backdrops.
 *
 * Five, not fifty. The point is to get to a good result without opening a
 * colour picker; anyone who wants their exact brand colour has the fields
 * below. `swatch` repeats the CSS because the button previews the real thing.
 */
/** What a HEAD or END section can be set to. See `kinds` in `BookendPane`. */
const offeredKinds = ["card", "image"];

/** How each effect reads in a list. */
const EFFECT_LABELS: Record<FocusEffect["mode"], string> = {
  zoom: "Zoom",
  highlight: "Highlight",
  callout: "Callout",
  cursor: "Cursor",
  blur: "Blur",
};

const asOptions = (values: readonly string[]) =>
  values.map((value) => ({ value, label: value.replace(/-/g, " ").replace(/^\w/, (c) => c.toUpperCase()) }));

/** Two arrows crossing: the shuffle mark, drawn rather than typed as a glyph. */
const ShuffleIcon: React.FC = () => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 12 12"
    aria-hidden="true"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
  >
    <path d="M1 2.5h2.2l5.1 7h2.2" strokeLinecap="round" />
    <path d="M1 9.5h2.2l2.1-2.9" strokeLinecap="round" />
    <path d="M8.3 2.5h2.2" strokeLinecap="round" />
    <path d="M9.2 1.2l1.4 1.3-1.4 1.3" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9.2 8.2l1.4 1.3-1.4 1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** What a style takes, said in the panel so the strip is not a mystery. */
const PALETTE_USE: Record<string, string> = {
  color: "its ground",
  gradient: "its liveliest colour, against the furthest from it",
  pattern: "its ground and one field",
  mesh: "its ground and every field",
};

/**
 * Choosing what a section sits on.
 *
 * BODY, HEAD and END all carry a `background`, and all three are picked the
 * same way. One component so the three cannot drift into three slightly
 * different colour pickers.
 *
 * One route in, deliberately. There used to be a row of preset swatches *and* a
 * style dropdown, and the two could contradict each other: picking a two-colour
 * preset and then "Solid" silently kept the first of its colours and threw the
 * other away. The palette says what a background is made of, and the style says
 * what shape it takes.
 */
const PaletteRow: React.FC<{
  palette: Palette;
  /**
   * The colours this style will actually draw, not the whole palette.
   *
   * A four-swatch strip beside a two-colour gradient promises two colours it
   * never draws — and worse, promises the wrong two, since a gradient leads
   * with the liveliest colour rather than the first.
   */
  colours: readonly string[];
  selected: boolean;
  onPick: () => void;
}> = ({ palette, colours, selected, onPick }) => (
  <button
    type="button"
    data-palette={palette.id}
    aria-label={`Palette ${palette.id}`}
    aria-pressed={selected}
    onClick={onPick}
    className="rounded-[7px] border border-transparent p-[3px] transition-colors hover:bg-bay-800 aria-pressed:border-ember-500 aria-pressed:bg-ember-500/[0.07]"
  >
    <span
      className="flex h-[26px] overflow-hidden rounded-[5px]"
      style={{ boxShadow: "0 1px 2px rgba(16, 21, 28, 0.12)" }}
    >
      {colours.map((colour, index) => (
        <i key={`${colour}-${index}`} className="flex-1" style={{ background: colour }} />
      ))}
    </span>
    <span className="flex justify-between px-[2px] pt-1 text-[9.5px] font-semibold uppercase tracking-[0.05em] text-ink-400 aria-pressed:text-ink-100">
      <span>{palette.id}</span>
      {selected ? <span className="font-medium normal-case tracking-normal">{palette.family}</span> : null}
    </span>
  </button>
);

const SECTION_NAMES = { head: "HEAD", body: "BODY", end: "END" } as const;

/**
 * Offers the background you just chose to the other sections.
 *
 * Backgrounds are picked one section at a time, and a video whose HEAD, BODY
 * and END disagree looks like three videos. Setting the same thing three times
 * by hand is the kind of work the editor should be doing.
 *
 * It appears whenever the sections disagree rather than firing on every change:
 * the colour pickers commit continuously, so a prompt tied to "changed" would
 * reappear on every step of a drag. Dismissing it is remembered against the
 * background it was dismissed for, so it stays gone until you pick something
 * new — rather than never coming back, which would strand anyone who dismissed
 * it once.
 */
const BackgroundSpread: React.FC<{
  timeline: Timeline;
  which: "head" | "body" | "end";
  commit: InspectorProps["commit"];
}> = ({ timeline, which, commit }) => {
  const [dismissed, setDismissed] = useState<string | null>(null);
  const mine = timeline.sections[which]?.background;
  const signature = mine ? JSON.stringify(mine) : "";

  // Only sections that exist, are switched on, and are actually wearing
  // something else. A disabled END is not a section anyone is looking at.
  const others = (["head", "body", "end"] as const).filter((other) => {
    if (other === which) return false;
    const section = timeline.sections[other];
    if (!section || section.enabled === false) return false;
    return JSON.stringify(section.background) !== signature;
  });

  if (!mine || others.length === 0 || dismissed === signature) return null;

  const names = others.map((other) => SECTION_NAMES[other]);
  const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

  return (
    /* Stacked, not side by side. The inspector is a narrow column, and a
       sentence sharing a row with two buttons wrapped onto four lines — "Use
       this / background / for HEAD and / END too?" — which is a question that
       has to be read twice. On its own line it is one. */
    <div className="flex flex-col gap-2 rounded-[6px] border border-ember-600/40 bg-ember-600/10 px-2.5 py-2">
      <span className="text-[11px] leading-snug text-ink-200">Use this background for {list} too?</span>
      <span className="flex items-center gap-1.5">
        <button
          className="btn btn-secondary btn-sm"
          onClick={() =>
            commit((draft) => {
              for (const other of others) {
                const section = draft.sections[other];
                // Cloned per section: one shared object would make a later edit
                // to one section silently change the others.
                if (section) section.background = structuredClone(mine);
              }
              return draft;
            })
          }
        >
          Apply
        </button>
        <button
          className="btn btn-ghost btn-sm"
          title="Keep the sections different"
          onClick={() => setDismissed(signature)}
        >
          No
        </button>
      </span>
    </div>
  );
};

/** Two backgrounds are the same when every field of them is. */
const sameBackground = (a: Background, b: Background) => JSON.stringify(a) === JSON.stringify(b);

const BackdropControls: React.FC<{
  background: Background;
  onChange: (next: Background) => void;
  /** Needed only to upload a background image. */
  projectId: string;
  /** The workspace's colours, when somebody has set them. */
  brand?: BrandKit | null;
}> = ({ background, onChange, projectId, brand }) => {
  // Which palette drew what is on screen, matched on the colours rather than
  // stored beside them. Storing an id would be a second source of truth that
  // goes stale the moment someone edits one colour, and the panel would keep
  // claiming a palette the card no longer wears.
  const chosen = paletteBehind(background, palettes);

  const [family, setFamily] = useState<PaletteFamily | "All">("All");
  const shown = family === "All" ? palettes : palettes.filter((palette) => palette.family === family);

  // Open on the palette the card is wearing rather than on the top of the
  // list. Out of 186 rows the selected one is almost never the first, and a
  // highlight you have to go looking for is not a highlight.
  //
  // scrollTop rather than scrollIntoView, which would drag the whole inspector
  // to put a row in the middle of the screen.
  const listRef = useRef<HTMLDivElement>(null);
  const chosenId = chosen?.id;
  useEffect(() => {
    const list = listRef.current;
    if (!list || chosenId === undefined) return;
    const row = list.querySelector<HTMLElement>(`[data-palette="${chosenId}"]`);
    if (!row) return;
    const top = row.offsetTop - list.offsetTop;
    if (top < list.scrollTop || top + row.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTop = Math.max(0, top - 6);
    }
  }, [chosenId, family]);

  /**
   * "Image" chosen, but no picture uploaded yet.
   *
   * An image background needs an asset key and the schema will not accept one
   * without it, so picking Image cannot commit anything on its own. Rather than
   * snapping the menu back to the old style — which reads as the control
   * refusing the choice — the menu holds on Image and the uploader appears; the
   * background is replaced the moment a file lands.
   */
  const [wantImage, setWantImage] = useState(background.kind === "image");
  useEffect(() => {
    if (background.kind === "image") setWantImage(true);
  }, [background.kind]);

  const kind = background.kind === "image" || wantImage ? "image" : background.kind;
  // The palette list and the shape pickers describe colours, and an image has
  // none to choose.
  const chromatic = kind !== "image";

  return (
    <>
      {/* Called "Style" until now, which named the control rather than the
          thing. What you are choosing is the background; solid, gradient,
          pattern, mesh and image are how it is made. The body panel already
          called it that, so this is also what makes the three sections agree. */}
      <Field label="Background">
        <Select
          value={kind}
          options={[
            { value: "color", label: "Solid" },
            { value: "gradient", label: "Gradient" },
            { value: "pattern", label: "Pattern" },
            { value: "mesh", label: "Mesh" },
            { value: "image", label: "Image" },
          ]}
          onChange={(value) => {
            if (value === "image") {
              setWantImage(true);
              return;
            }
            setWantImage(false);
            // Carried by the palette rather than by a colour pair: a pair
            // survives only the two styles that happen to share field names,
            // and a mesh has no `from` to hand on.
            const palette = chosen ?? palettes[0];
            onChange(paletteBackground(value as Background["kind"], palette));
          }}
        />
      </Field>

      {kind === "image" ? (
        <>
          <BookendImage
            projectId={projectId}
            assetKey={background.kind === "image" ? background.assetKey : undefined}
            onAssetKey={(key) =>
              onChange({
                kind: "image",
                assetKey: key,
                dim: background.kind === "image" ? background.dim : 0.35,
              })
            }
          />
          {background.kind === "image" ? (
            <Slider
              label="Dim"
              value={Math.round(background.dim * 100)}
              min={0}
              max={90}
              step={5}
              suffix="%"
              // Text over a photograph is unreadable without it, so this is the
              // control that makes an image background usable rather than a
              // decoration that eats the words.
              onChange={(value) => onChange({ ...background, dim: value / 100 })}
            />
          ) : null}
        </>
      ) : null}

      {background.kind === "mesh" ? (
        <div className="flex flex-col gap-1.5">
          <span className="text-[11px] font-medium text-ink-300">Arrangement</span>
          {/* Drawn by the renderer's own component, so a thumbnail cannot
              promise something the render will not draw. */}
          <div className="grid grid-cols-3 gap-1.5">
            {meshLayouts.map((layout) => (
              <button
                key={layout}
                title={meshLayoutLabels[layout]}
                aria-label={meshLayoutLabels[layout]}
                data-selected={layout === background.layout}
                onClick={() => onChange({ ...background, layout })}
                className="relative overflow-hidden rounded-[5px] border border-[var(--edge)] transition-transform hover:scale-[1.03] data-[selected=true]:border-ember-500 data-[selected=true]:ring-1 data-[selected=true]:ring-ember-500/40"
                style={{ aspectRatio: "16 / 9" }}
              >
                <MeshLayer layout={layout} ground={background.ground} fields={background.fields} />
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {background.kind === "pattern" ? (
        <div className="flex flex-col gap-1.5">
          <span className="text-[11px] font-medium text-ink-300">Pattern</span>
          <div className="grid grid-cols-3 gap-1.5">
            {backgroundPatterns.map((pattern) => (
              <button
                key={pattern}
                title={backgroundPatternLabels[pattern]}
                aria-label={backgroundPatternLabels[pattern]}
                data-selected={pattern === background.pattern}
                onClick={() => onChange({ ...background, pattern })}
                className="relative overflow-hidden rounded-[5px] border border-[var(--edge)] transition-transform hover:scale-[1.03] data-[selected=true]:border-ember-500 data-[selected=true]:ring-1 data-[selected=true]:ring-ember-500/40"
                style={{ aspectRatio: "16 / 9" }}
              >
                <PatternLayer pattern={pattern} from={background.from} to={background.to} />
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {/* A palette is a set of colours, and an image has none to pick. Left
          visible it would also index PALETTE_USE on a kind that has no entry. */}
      {/* The workspace's own colours, above the library and outside its filters.
          They are the one palette that is not a suggestion, and until now there
          was no way to reach them for anything but a flat colour. */}
      {chromatic && brand && isBrandConfigured(brand) ? (
        <div
          data-selected={sameBackground(background, brandBackgroundFor(kind, brand))}
          className="flex items-center gap-2 rounded-[6px] border border-[var(--edge)] p-1.5 transition-colors data-[selected=true]:border-ember-500 data-[selected=true]:ring-1 data-[selected=true]:ring-ember-500/40"
        >
          <button
            type="button"
            onClick={() => onChange(brandBackgroundFor(kind, brand))}
            className="flex min-w-0 flex-1 items-center gap-2 rounded-[4px] text-left transition-colors hover:opacity-80"
            title="Your brand colours, in this style"
          >
            <span className="flex shrink-0 overflow-hidden rounded-[4px]">
              {brandPalette(brand).colours.map((colour) => (
                <span key={colour} className="h-5 w-5" style={{ backgroundColor: colour }} />
              ))}
            </span>
            <span className="text-[11px] font-medium text-ink-200">Brand</span>
          </button>

          {/* The colours themselves are changed in one place, and this is the
              screen you are on when you want to. Separate from the swatches so
              reaching the settings and applying the palette stay two different
              acts — a row that navigated could never be selected. */}
          <Link
            to="/settings#brand"
            title="Edit the brand kit"
            aria-label="Edit the brand kit"
            className="shrink-0 rounded-[4px] px-1.5 py-0.5 text-[11px] text-ink-400 transition-colors hover:bg-bay-800 hover:text-ink-100"
          >
            Edit
          </Link>
        </div>
      ) : null}

      {chromatic ? (
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium text-ink-300">Palette</span>
            {/* 186 rows is a lot of scrolling to find out what you like. This is
              the other way to use the list: keep pressing until something on
              the card stops you. Never lands on the one already showing, since
              a shuffle that changes nothing reads as a broken button. */}
            <button
              type="button"
              title="Try another palette"
              aria-label="Try another palette"
              onClick={() => {
                const pool = shown.filter((palette) => palette.id !== chosen?.id);
                if (pool.length === 0) return;
                onChange(paletteBackground(kind, pool[Math.floor(Math.random() * pool.length)]));
              }}
              className="flex items-center gap-1 rounded-[5px] border border-[var(--edge)] px-1.5 py-[2px] text-[10px] font-medium text-ink-300 transition-colors hover:bg-bay-800 hover:text-ink-100"
            >
              <ShuffleIcon />
              Shuffle
            </button>
          </div>
          {/* A flat list of every palette is five thousand pixels of scroll,
            which is a list nobody reaches the end of. The families are read
            off each palette's own liveliest colour, so the next hundred file
            themselves. */}
          <div className="flex flex-wrap gap-1">
            {(["All", ...paletteFamilies] as const).map((entry) => (
              <button
                key={entry}
                type="button"
                aria-pressed={entry === family}
                onClick={() => setFamily(entry)}
                className="rounded-full border border-[var(--edge)] bg-bay-850 px-[7px] py-[2px] text-[9.5px] font-semibold uppercase tracking-[0.05em] text-ink-300 transition-colors hover:bg-bay-800 aria-pressed:border-ink-100 aria-pressed:bg-ink-100 aria-pressed:text-bay-850"
              >
                {entry}
              </button>
            ))}
          </div>
          <div ref={listRef} className="flex max-h-[214px] flex-col gap-1 overflow-y-auto pr-0.5">
            {shown.map((palette) => (
              <PaletteRow
                key={palette.id}
                palette={palette}
                colours={backgroundColours(paletteBackground(kind, palette))}
                selected={palette.id === chosen?.id}
                onPick={() => onChange(paletteBackground(kind, palette))}
              />
            ))}
          </div>
          <p className="text-[10px] leading-snug text-ink-400">
            This style takes {PALETTE_USE[kind]}. Every colour stays editable — a palette sets them, it does not lock
            them.
          </p>
        </div>
      ) : null}

      {background.kind === "color" ? (
        <ColorField label="Colour" value={background.color} onChange={(color) => onChange({ kind: "color", color })} />
      ) : null}

      {background.kind === "mesh" ? (
        <>
          <ColorField
            label="Ground"
            value={background.ground}
            onChange={(ground) => onChange({ ...background, ground })}
          />
          {background.fields.map((colour, index) => (
            <ColorField
              key={index}
              label={`Field ${index + 1}`}
              value={colour}
              onChange={(next) =>
                onChange({
                  ...background,
                  fields: background.fields.map((was, at) => (at === index ? next : was)),
                })
              }
            />
          ))}
        </>
      ) : null}

      {background.kind === "gradient" || background.kind === "pattern" ? (
        <>
          <ColorField
            label="From"
            value={background.from}
            onChange={(next) => onChange({ ...background, from: next })}
          />
          <ColorField label="To" value={background.to} onChange={(next) => onChange({ ...background, to: next })} />
        </>
      ) : null}

      {background.kind === "gradient" ? (
        <Slider
          label="Angle"
          value={background.angle}
          min={0}
          max={360}
          step={5}
          suffix="°"
          onChange={(angle) => onChange({ ...background, angle })}
        />
      ) : null}
    </>
  );
};

/**
 * Setting one line of a card.
 *
 * The three buttons are a segmented row rather than three toggles because bold,
 * italic and strikethrough are one decision about one piece of text, and three
 * full-width switches would read as three unrelated settings.
 *
 * Every control shows the value the renderer would use, taken from
 * `bookendTextDefaults` when nothing has been set, so the panel never disagrees
 * with what is on screen. Touching a control writes that one field and leaves
 * the rest unset.
 */
/**
 * The font picker.
 *
 * The library ships with the application, so this is a list of faces that will
 * actually be drawn rather than a list of names the encoder would substitute.
 * Grouped, because twenty in one flat list is a scroll rather than a choice.
 *
 * Two things it has to get right. A card written before the library existed
 * carries a stack that is not in it, and a select whose value matches no option
 * renders blank — so an unknown stack is carried along as its own group rather
 * than silently losing the card's font. And the sample underneath is drawn at
 * the weight the card is actually set in, resolved the same way the renderer
 * resolves it, so a display face that only has 400 previews as 400 here too.
 */
const FontField: React.FC<{
  stack: string;
  weight: number;
  italic: boolean;
  onChange: (stack: string) => void;
  projectId: string;
  fonts: ProjectFont[];
  onAddFont: (font: ProjectFont) => void;
}> = ({ stack, weight, italic, onChange, projectId, fonts, onAddFont }) => {
  const known = findBookendFont(stack);
  const family = primaryFamily(stack);
  const uploaded = fonts.find((font) => font.family === family);
  const shown = known ? nearestWeight(known.weights, weight) : weight;
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileRef = React.useRef<HTMLInputElement>(null);

  const takeFile = (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    api
      .uploadFont(projectId, file)
      .then((added) => {
        onAddFont({ family: added.family, assetKey: added.key });
        onChange(uploadedFontStack(added.family));
      })
      .catch((error: Error) => setUploadError(error.message))
      .finally(() => setUploading(false));
  };

  return (
    <div className="flex flex-col gap-1.5">
      <Field label="Font">
        <select className="input cursor-pointer" value={stack} onChange={(event) => onChange(event.target.value)}>
          {/* A stack from before the library, or one whose upload has been
              removed: a select whose value matches no option renders blank and
              loses the card's font without saying so. */}
          {!known && !uploaded && (
            <optgroup label="On this card">
              <option value={stack}>{family || "Custom"}</option>
            </optgroup>
          )}
          {fonts.length > 0 && (
            <optgroup label="Uploaded">
              {fonts.map((font) => (
                <option key={font.assetKey} value={uploadedFontStack(font.family)}>
                  {font.family}
                </option>
              ))}
            </optgroup>
          )}
          {bookendFontCategories.map((category) => (
            <optgroup key={category} label={category}>
              {bookendFonts
                .filter((font) => font.category === category)
                .map((font) => (
                  <option key={font.id} value={font.stack} style={{ fontFamily: font.stack }}>
                    {font.family}
                  </option>
                ))}
            </optgroup>
          ))}
        </select>
      </Field>
      <div
        className="truncate rounded-[5px] border border-[var(--edge)] bg-bay-900 px-2.5 py-2 text-ink-100"
        style={{
          fontFamily: stack,
          fontWeight: shown,
          fontStyle: italic ? "italic" : "normal",
          fontSize: 19,
          lineHeight: 1.25,
        }}
        title={known ? `${known.family} ${shown}` : stack}
      >
        The quick brown fox
      </div>
      {known && shown !== weight && (
        <span className="text-[10.5px] text-ink-400">
          {known.family} ships {known.weights.join(", ")} — drawn at {shown}.
        </span>
      )}

      <input
        ref={fileRef}
        type="file"
        accept=".woff2,.woff,.ttf,.otf"
        className="hidden"
        onChange={(event) => {
          takeFile(event.target.files?.[0]);
          event.target.value = "";
        }}
      />
      <button
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        className="self-start text-[10.5px] text-ink-400 underline-offset-2 transition-colors hover:text-ink-200 hover:underline disabled:opacity-50"
      >
        {uploading ? "Uploading…" : "Upload a font"}
      </button>
      {uploadError ? <span className="text-[10.5px] text-ember-400">{uploadError}</span> : null}
      {uploaded ? (
        <span className="text-[10.5px] leading-snug text-ink-400">
          Uploaded to this project. Embedding a font is on whoever holds its licence.
        </span>
      ) : null}
    </div>
  );
};

const TextStyleControls: React.FC<{
  which: "title" | "subtitle";
  style: TextStyle | undefined;
  onChange: (patch: Partial<TextStyle>) => void;
  projectId: string;
  fonts: ProjectFont[];
  onAddFont: (font: ProjectFont) => void;
}> = ({ which, style, onChange, projectId, fonts, onAddFont }) => {
  const fallback = bookendTextDefaults[which];
  const marks = [
    { key: "bold", label: "B", title: "Bold", className: "font-bold" },
    { key: "italic", label: "I", title: "Italic", className: "italic font-serif" },
    { key: "strikethrough", label: "S", title: "Strikethrough", className: "line-through" },
  ] as const;

  return (
    <div className="flex flex-col gap-3 rounded-[6px] border border-[var(--edge)] bg-bay-850 p-2.5">
      <Eyebrow>{which === "title" ? "Title style" : "Subtitle style"}</Eyebrow>

      <FontField
        stack={style?.fontFamily ?? fallback.fontFamily}
        weight={(style?.bold ?? fallback.bold) ? fallback.weights.bold : fallback.weights.normal}
        italic={style?.italic ?? fallback.italic}
        onChange={(fontFamily) => onChange({ fontFamily })}
        projectId={projectId}
        fonts={fonts}
        onAddFont={onAddFont}
      />

      <Slider
        label="Size"
        value={style?.fontSize ?? fallback.fontSize}
        min={which === "title" ? 24 : 12}
        max={which === "title" ? 200 : 120}
        step={2}
        suffix="px"
        onChange={(fontSize) => onChange({ fontSize })}
      />

      <div className="flex flex-col gap-1.5">
        <span className="text-[11px] font-medium text-ink-300">Emphasis</span>
        <div className="flex gap-1.5">
          {marks.map((mark) => {
            const on = style?.[mark.key] ?? fallback[mark.key];
            return (
              <button
                key={mark.key}
                title={mark.title}
                aria-label={mark.title}
                aria-pressed={on}
                onClick={() => onChange({ [mark.key]: !on })}
                data-selected={on}
                className="h-[30px] w-[34px] rounded-[5px] border border-[var(--edge)] text-[13px] text-ink-300 transition-colors hover:bg-bay-800 hover:text-ink-100 data-[selected=true]:border-ember-500/50 data-[selected=true]:bg-bay-750 data-[selected=true]:text-ink-100"
              >
                <span className={mark.className}>{mark.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <ColorField
        label="Text colour"
        value={style?.color ?? fallback.color}
        onChange={(color) => onChange({ color })}
      />
    </div>
  );
};

/**
 * Choosing the still a card shows.
 *
 * The image kind existed before this did, with its key hard-coded to a
 * placeholder that was never uploaded — the type could be picked and then
 * nothing could be done with it. The upload is here rather than on a separate
 * assets screen because the only reason to have this file is the card in front
 * of you.
 */
const BookendImage: React.FC<{
  projectId: string;
  assetKey: string | undefined;
  onAssetKey: (key: string) => void;
}> = ({ projectId, assetKey, onAssetKey }) => {
  const input = React.useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const choose = (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    void api
      .uploadImage(projectId, file)
      .then((result) => onAssetKey(result.key))
      .catch((cause: Error) => setError(cause.message))
      .finally(() => {
        setBusy(false);
        // Clearing it lets the same file be picked again after a failure.
        if (input.current) input.current.value = "";
      });
  };

  return (
    <div className="flex flex-col gap-2">
      <span className="text-[11px] font-medium text-ink-300">Image</span>

      {assetKey ? (
        <img
          src={`/assets/${assetKey}`}
          alt=""
          className="h-[92px] w-full rounded-[5px] border border-[var(--edge)] bg-bay-900 object-cover"
        />
      ) : (
        <div className="flex h-[92px] items-center justify-center rounded-[5px] border border-dashed border-[var(--edge)] bg-bay-900 text-[11px] text-ink-400">
          No image yet
        </div>
      )}

      <input
        ref={input}
        type="file"
        accept={allowedImageMimeTypes.join(",")}
        className="hidden"
        onChange={(event) => choose(event.target.files?.[0])}
      />
      <button className="btn btn-secondary btn-sm self-start" disabled={busy} onClick={() => input.current?.click()}>
        {busy ? "Uploading…" : assetKey ? "Replace image" : "Choose image"}
      </button>
      {error ? <span className="text-[11px] text-signal-bad">{error}</span> : null}
    </div>
  );
};

/** HEAD and END are configured identically; only their position differs. */
/**
 * Narrating one section, from the section.
 *
 * Every section used to be voiced by one button above the body script, so
 * narrating an END card re-voiced the whole video to reach it. The narrator
 * belongs here too: the timeline has always carried a `voiceId` per section and
 * nothing ever let anyone set it.
 */
const SectionVoice: React.FC<{
  projectId: string;
  which: "head" | "body" | "end";
  voiceId: string | undefined;
  onVoiceId: (value: string | undefined) => void;
  onRebuilt: () => void;
  disabled?: boolean;
  /** The body's narrator is chosen in the script panel; two pickers on one
   *  field would be two places to look and one to forget. */
  hideNarrator?: boolean;
  /**
   * Narration that already exists for this section.
   *
   * The mute and nudge controls only appear once there is something to mute or
   * nudge — before that they are two controls that visibly do nothing.
   */
  voice?: {
    enabled: boolean;
    onEnabled: (value: boolean) => void;
    offset: number;
    onOffset: (value: number) => void;
  };
}> = ({ projectId, which, voiceId, onVoiceId, onRebuilt, disabled, hideNarrator, voice }) => {
  const [voices, setVoices] = useState<{ id: string; name: string; freeTier: boolean }[]>([]);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .voices(projectId)
      .then((result) => !cancelled && setVoices(result.voices))
      // An empty list is a configuration problem the settings page reports; it
      // should not put an error on a panel about a card.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  return (
    <div className="flex flex-col gap-2 border-t border-[var(--edge)] pt-4">
      {voices.length > 0 && !hideNarrator ? (
        <Field label="Narrator" hint="Leave on the project default to follow the voice chosen in the script panel.">
          <select
            className="input !py-1 text-[12px]"
            value={voiceId ?? ""}
            onChange={(event) => onVoiceId(event.target.value || undefined)}
          >
            <option value="">Project default</option>
            {voices.map((voice) => (
              <option key={voice.id} value={voice.id}>
                {voice.name}
              </option>
            ))}
          </select>
        </Field>
      ) : null}

      <button
        className="btn btn-secondary btn-sm self-start"
        disabled={busy || disabled}
        title={`Narrate the ${which.toUpperCase()} section only, leaving the rest of the video alone`}
        onClick={() => {
          setBusy(true);
          setNote(null);
          void api
            .generateVoice(projectId, voiceId, [which])
            .then(() => {
              setNote("Queued. It will play once the narration lands.");
              onRebuilt();
            })
            .catch((cause: Error) => setNote(cause.message))
            .finally(() => setBusy(false));
        }}
      >
        {busy ? "Queueing…" : `Voice the ${which.toUpperCase()}`}
      </button>
      {note ? <span className="text-[11px] text-ink-400">{note}</span> : null}

      {voice ? (
        <div className="mt-1 flex flex-col gap-2 border-t border-[var(--edge)] pt-3">
          <Toggle
            label={`Play the ${which.toUpperCase()} narration`}
            checked={voice.enabled}
            onChange={voice.onEnabled}
            hint="Off keeps the audio but does not play it, so turning it back on costs nothing."
          />
          {voice.enabled ? (
            <Slider
              label="Nudge"
              value={voice.offset}
              min={-5}
              max={5}
              step={0.1}
              suffix="s"
              onChange={voice.onOffset}
            />
          ) : null}
          {voice.enabled ? (
            <p className="text-[11px] text-ink-400">
              It already waits for the {which === "end" ? "body" : "previous"} narration to finish. This is for when
              that is not what you wanted; it is always pulled back inside the card.
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

/**
 * Where an element sits against the card.
 *
 * Nine points and nothing else, because the ask was left, right, middle, top
 * and bottom and a grid says all nine at once in the space a dropdown would
 * take for one. It is an anchor rather than a snap: picking a point does not
 * move the box once and let go, it pins that edge, so a line that gets longer
 * grows inwards instead of walking off the card. Dragging on the frame breaks
 * the pin, and the control says so rather than silently showing nothing lit.
 */
/**
 * Ready-made cards, previewed with their motion.
 *
 * A grid rather than a dropdown because the thing you are choosing is how it
 * looks, and a still thumbnail cannot tell you how it arrives — so each tile
 * replays its own entrance on hover. One at a time: eight tiles looping at once
 * is a fairground, and it makes the one you are looking at harder to judge, not
 * easier. Nothing moves at all under `prefers-reduced-motion`.
 */
const PresetGallery: React.FC<{
  preset: CardPresetRef | undefined;
  onPick: (id: string) => void;
}> = ({ preset, onPick }) => {
  const from = findCardPreset(preset?.id);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <Eyebrow>Preset</Eyebrow>
        <span className="text-[10.5px] text-ink-400">
          {!from ? "None" : preset?.edited ? `Custom, from ${from.label}` : from.label}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-1.5">
        {cardPresets.map((option) => (
          <button
            key={option.id}
            onClick={() => onPick(option.id)}
            title={option.label}
            data-selected={preset?.id === option.id && !preset?.edited}
            className="group overflow-hidden rounded-[6px] border border-[var(--edge)] transition-colors hover:border-[var(--edge-strong)] data-[selected=true]:border-ember-500/70"
          >
            <div className="relative aspect-video overflow-hidden">
              <PresetFace preset={option} />
            </div>
            <span className="block truncate px-1.5 py-1 text-left text-[10px] text-ink-400">{option.label}</span>
          </button>
        ))}
      </div>

      <p className="text-[10.5px] leading-snug text-ink-400">
        A preset sets the layout, type, backdrop and motion. It never changes your words.
      </p>
    </div>
  );
};

/**
 * A preset drawn small.
 *
 * The real background component, so a tile cannot promise a backdrop the render
 * will not draw — the same rule the pattern picker already follows. The text is
 * bars rather than words: at 90px wide a real sentence is a smudge, and what
 * this has to show is placement and timing.
 */
const PresetFace: React.FC<{ preset: (typeof cardPresets)[number] }> = ({ preset }) => {
  // The same card-wide layout the renderer uses, so a tile cannot promise a
  // placement the render will not draw — the rule the pattern picker already
  // follows for backdrops.
  const boxes = anchoredBoxes(
    preset.roles.map((role, index) => ({
      id: String(index),
      kind: "text" as const,
      text: "",
      role: "title" as const,
      align: role.align,
      anchor: role.anchor,
      x: 0,
      y: 0,
      width: role.width,
      height: role.height,
      motion: role.motion,
    })),
  );

  return (
    <>
      {preset.background.kind === "pattern" ? (
        <PatternLayer pattern={preset.background.pattern} from={preset.background.from} to={preset.background.to} />
      ) : (
        <div
          className="absolute inset-0"
          style={{ background: preset.background.kind === "color" ? preset.background.color : "#0b1020" }}
        />
      )}
      {preset.roles.map((role, index) => (
        <span
          key={index}
          className="preset-bar absolute rounded-[1px]"
          data-entry={role.motion.entry.type}
          style={{
            left: `${boxes[index].x * 100}%`,
            top: `${(boxes[index].y + boxes[index].height / 2) * 100}%`,
            width: `${role.width * 100}%`,
            height: Math.max(2, role.fontSize / 26),
            background: role.color,
            opacity: index === 0 ? 1 : 0.6,
            animationDelay: `${role.motion.delay}s`,
          }}
        />
      ))}
    </>
  );
};

/**
 * One level of the panel.
 *
 * The HEAD properties hold three different kinds of thing — settings for the
 * section, for the card, and for whichever element is selected — and used to
 * present all three as one flat column. Nothing said which level a control was
 * on, so `Size` (one line) and `Duration` (the whole section) looked identical
 * and sat a screen and a half apart, with the section's own settings appearing
 * *below* an element's.
 *
 * A band names its level and summarises itself, so a glance answers "what am I
 * changing?" without reading the controls to work it out.
 */
/**
 * One header treatment for every band.
 *
 * Bands were once coloured by level — a section holding a card holding a line
 * — but nothing on screen said that was the code, and the nesting is already
 * carried by the order and the labels. What the colour actually did was make
 * the panel loud, so the header is quiet and the reading is left to the words.
 */
const BAND_HEAD = "border-[var(--edge)] bg-bay-800 text-ink-300";

const Band: React.FC<{
  title: string;
  summary?: string;
  /**
   * Whether the band folds away, and whether it starts folded.
   *
   * The section's settings are chosen once and then read at a glance — how long
   * the card stays, what is said over it — so leaving them expanded puts a
   * screen of answered questions above the things that are edited constantly.
   * Folded, the summary carries what matters and the preset grid is the first
   * thing on the panel, which is what someone opening a card is looking for.
   */
  collapsible?: boolean;
  startOpen?: boolean;
  children: React.ReactNode;
}> = ({ title, summary, collapsible = false, startOpen = true, children }) => {
  const [open, setOpen] = useState(startOpen);
  const shown = !collapsible || open;

  const heading = (
    <>
      {collapsible ? (
        <span aria-hidden className={`text-[8px] transition-transform ${shown ? "" : "-rotate-90"}`}>
          ▾
        </span>
      ) : null}
      <span className="text-[9.5px] font-bold uppercase tracking-[0.1em]">{title}</span>
      {/* The summary is what a folded band says about itself, so it is never
          hidden — a band you have to open to find out what is in it is a band
          you will keep opening. */}
      {summary ? <span className="ml-auto truncate text-[10.5px] text-ink-400">{summary}</span> : null}
    </>
  );

  return (
    <section className="overflow-hidden rounded-[8px] border border-[var(--edge-strong)]">
      {collapsible ? (
        <button
          type="button"
          onClick={() => setOpen((was) => !was)}
          aria-expanded={open}
          className={`flex w-full items-center gap-2 px-2.5 py-1.5 text-left transition-colors ${BAND_HEAD} ${
            shown ? "border-b" : ""
          }`}
        >
          {heading}
        </button>
      ) : (
        <header className={`flex items-center gap-2 border-b px-2.5 py-1.5 ${BAND_HEAD}`}>{heading}</header>
      )}
      {shown ? <div className="flex flex-col gap-3 p-2.5">{children}</div> : null}
    </section>
  );
};

/**
 * A group inside a band.
 *
 * Deliberately quieter than a band header and indented past a spine in the
 * band's colour. The first attempt gave these uppercase headings with a rule
 * across the panel — which is what a band header looks like, so "MOTION" read
 * as a level of its own and the honest question became whether it moved the
 * card or the line. Where you are has to be answerable from any scroll
 * position.
 */
/**
 * The presets, and "None" as one of them.
 *
 * None is a choice rather than the absence of one — it is what almost every
 * card should keep, and putting it in the same row makes going back a click
 * rather than a hunt for how to undo.
 */
const SHADOW_CHOICES: Array<{ key: "none" | CardShadowPreset; label: string }> = [
  { key: "none", label: "None" },
  { key: "soft", label: "Soft" },
  { key: "lifted", label: "Lifted" },
  { key: "deep", label: "Deep" },
  { key: "edge", label: "Edge" },
];

const Group: React.FC<{ title: string; note?: string; children: React.ReactNode }> = ({ title, note, children }) => (
  <div className="border-l-2 border-signal-ok/30 pl-2.5">
    <div className="mb-1.5 flex items-center gap-1.5">
      <span className="text-[11px] font-semibold text-ink-200">{title}</span>
      {note ? <span className="ml-auto text-[9.5px] text-ink-400">{note}</span> : null}
    </div>
    <div className="flex flex-col gap-2.5">{children}</div>
  </div>
);

/** One word for what an element is, in the layer list. */
const elementIcon = (element: CardElement): string =>
  element.kind === "text" ? "T" : element.kind === "image" ? "▣" : "◆";

const elementName = (element: CardElement): React.ReactNode => {
  if (element.kind === "text") {
    return element.text.trim() || <span className="text-ink-400">Empty line</span>;
  }
  if (element.kind === "image") {
    return element.assetKey ? "Image" : <span className="text-ink-400">Image — none chosen</span>;
  }
  return element.shape.charAt(0).toUpperCase() + element.shape.slice(1);
};

/**
 * What can be put on a card.
 *
 * A menu rather than a row of buttons: there are six things and only one of
 * them gets used most of the time, so the common one is the label and the rest
 * are one click away instead of six buttons competing for the same glance.
 */
/** Matches the menu's own width, used to right-align it against the button. */
const MENU_WIDTH = 150;

const InsertMenu: React.FC<{ onInsert: (what: "text" | "image" | CardShape) => void }> = ({ onInsert }) => {
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const [at, setAt] = useState<{ left: number; top: number } | null>(null);
  const items: { key: "text" | "image" | CardShape; label: string; icon: string }[] = [
    { key: "text", label: "Text", icon: "T" },
    { key: "image", label: "Image", icon: "▣" },
    { key: "rect", label: "Rectangle", icon: "▭" },
    { key: "ellipse", label: "Ellipse", icon: "◯" },
    { key: "line", label: "Line", icon: "╱" },
    { key: "arrow", label: "Arrow", icon: "→" },
  ];

  /**
   * Placed against the viewport rather than the button.
   *
   * The menu was absolutely positioned inside the inspector, which is an
   * `overflow-y-auto` column — so the panel edge clipped it and the lower half
   * of the list could not be seen at all. Through a portal at fixed coordinates
   * it belongs to no scroll container, and it opens upwards when there is not
   * room below.
   */
  const place = () => {
    const box = trigger.current?.getBoundingClientRect();
    if (!box) return;
    const height = items.length * 30 + 8;
    const room = window.innerHeight - box.bottom;
    setAt({
      left: Math.max(8, box.right - MENU_WIDTH),
      top: room < height + 8 ? Math.max(8, box.top - height - 4) : box.bottom + 4,
    });
  };

  return (
    <div className="relative">
      <button
        ref={trigger}
        onClick={() => {
          if (!open) place();
          setOpen((was) => !was);
        }}
        aria-expanded={open}
        className="rounded-[5px] border border-[var(--edge)] px-2 py-1 text-[11px] text-ink-300 transition-colors hover:bg-bay-800 hover:text-ink-100"
      >
        + Insert
      </button>
      {open && at
        ? createPortal(
            <>
              {/* Click anywhere else to dismiss. A menu that can only be closed by
                  choosing something is a menu you cannot back out of. */}
              <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />
              <div
                className="fixed z-[61] w-[150px] overflow-hidden rounded-[6px] border border-[var(--edge-strong)] bg-bay-850 py-1 shadow-lg"
                style={{ left: at.left, top: at.top }}
              >
                {items.map((item) => (
                  <button
                    key={item.key}
                    onClick={() => {
                      onInsert(item.key);
                      setOpen(false);
                    }}
                    className="flex w-full items-center gap-2.5 px-2.5 py-1.5 text-left text-[12px] text-ink-200 transition-colors hover:bg-bay-750 hover:text-ink-100"
                  >
                    <span className="w-3 text-center text-[11px] text-ink-400">{item.icon}</span>
                    {item.label}
                  </button>
                ))}
              </div>
            </>,
            document.body,
          )
        : null}
    </div>
  );
};

/**
 * Entry, exit and a delay, per element.
 *
 * Off by default, and off means something: an element with no motion of its own
 * moves with the card's Entrance, so an untouched card is untouched. Turning it
 * on takes that element out of the card's control entirely rather than layering
 * a second movement on top of the first — the mistake hold-and-fade made.
 *
 * The warning is not decoration. A fade or a delay means the element is not on
 * frame zero, which is the frame a player shows as the thumbnail; that has
 * shipped as a blank title card here once already.
 */
const MotionControls: React.FC<{
  motion: CardMotion;
  onChange: (motion: CardMotion) => void;
}> = ({ motion, onChange }) => {
  const costsFirstFrame = motion.delay > 0 || motion.entry.type === "fade";

  return (
    <div className="flex flex-col gap-2.5 rounded-[6px] border border-[var(--edge)] bg-bay-900 p-2.5">
      <Eyebrow>Motion</Eyebrow>

      <Slider
        label="Delay"
        value={motion.delay}
        min={0}
        max={3}
        step={0.1}
        suffix="s"
        onChange={(delay) => onChange({ ...motion, delay })}
      />

      {(["entry", "exit"] as const).map((phase) => (
        <div key={phase} className="flex items-end gap-2">
          <div className="flex-1">
            <Field label={phase === "entry" ? "Entry" : "Exit"}>
              <Select
                value={motion[phase].type}
                options={asOptions([...cardMotionTypes])}
                onChange={(type) =>
                  onChange({ ...motion, [phase]: { ...motion[phase], type: type as CardMotionType } })
                }
              />
            </Field>
          </div>
          <div className="w-[92px]">
            <Field label="Over">
              <Select
                value={String(motion[phase].duration)}
                options={["0.3", "0.5", "0.8", "1.2"].map((value) => ({ value, label: `${value}s` }))}
                onChange={(duration) =>
                  onChange({ ...motion, [phase]: { ...motion[phase], duration: Number(duration) } })
                }
              />
            </Field>
          </div>
        </div>
      ))}

      {costsFirstFrame ? (
        <p className="text-[10.5px] leading-snug text-ember-400">
          Not on the first frame — a fade or a delay means this is invisible where a player takes its thumbnail.
        </p>
      ) : null}
    </div>
  );
};

const AnchorGrid: React.FC<{
  anchor: CardElement["anchor"];
  onChange: (anchor: CardElement["anchor"]) => void;
}> = ({ anchor, onChange }) => {
  const points = cardAnchors.filter((point) => point !== "free");
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[11px] font-medium text-ink-300">Place on card</span>
      <div className="flex items-center gap-2.5">
        <div className="grid w-[74px] grid-cols-3 gap-[3px] rounded-[6px] border border-[var(--edge)] bg-bay-900 p-[3px]">
          {points.map((point) => (
            <button
              key={point}
              title={point.replace("-", " ")}
              aria-label={point.replace("-", " ")}
              aria-pressed={anchor === point}
              data-selected={anchor === point}
              onClick={() => onChange(point)}
              className="flex h-[20px] items-center justify-center rounded-[3px] transition-colors hover:bg-bay-750 data-[selected=true]:bg-ember-500/25"
            >
              <span className="h-[5px] w-[5px] rounded-full bg-ink-400 transition-colors" data-dot={anchor === point} />
            </button>
          ))}
        </div>
        <span className="text-[10.5px] leading-snug text-ink-400">
          {anchor === "free"
            ? "Placed by hand. Pick a point to pin it again."
            : "Pinned — it holds this edge as the text changes."}
        </span>
      </div>
    </div>
  );
};

const BookendPane: React.FC<{
  which: "head" | "end";
  scene: BookendScene | undefined;
  commit: InspectorProps["commit"];
  projectId: string;
  onRebuilt: () => void;
  selectedElementId: string | null;
  onSelectElement: (id: string | null) => void;
  fonts: ProjectFont[];
  onAddFont: (font: ProjectFont) => void;
  /** Whether narration audio has actually been generated for this card yet. */
  hasVoice: boolean;
  /** Only to compare this card's background against the other sections'. */
  timeline: Timeline;
  /** The workspace's colours, for the Brand row in the backdrop picker. */
  brand: BrandKit | null;
}> = ({
  which,
  scene,
  commit,
  projectId,
  onRebuilt,
  selectedElementId,
  onSelectElement,
  fonts,
  onAddFont,
  hasVoice,
  timeline,
  brand,
}) => {
  const enabled = Boolean(scene?.enabled);

  const update = (mutate: (draft: BookendScene) => void) =>
    commit((draft) => {
      const target = draft.sections[which];
      if (target) mutate(target);
      return draft;
    });

  const elements = scene?.content.kind === "card" ? scene.content.elements : [];
  /**
   * What a band says about itself in its header.
   *
   * So a glance answers what is inside without opening it — and, for the
   * selected element, which of several lines is being changed. Scrolling back
   * to the list to find that out was the thing that made the old panel hard to
   * hold in your head.
   */
  const cardPreset = scene?.content.kind === "card" ? findCardPreset(scene.content.preset?.id) : undefined;
  const cardSummary =
    scene?.content.kind !== "card"
      ? scene?.content.kind
      : [
          !cardPreset
            ? "no preset"
            : scene.content.preset?.edited
              ? `custom, from ${cardPreset.label}`
              : cardPreset.label,
          `${elements.length} ${elements.length === 1 ? "item" : "items"}`,
        ].join(" · ");
  const selected = elements.find((element) => element.id === selectedElementId) ?? elements[0];
  const selectedSummary = !selected
    ? undefined
    : selected.kind === "text"
      ? `“${selected.text.trim() || "empty line"}”`
      : selected.kind === "image"
        ? "Image"
        : selected.shape;

  /** Changes one field of one element, leaving the rest of the card alone. */
  const patchElement = (id: string, patch: Record<string, unknown>) =>
    update((target) => {
      if (target.content.kind !== "card") return;
      const element = target.content.elements.find((one) => one.id === id);
      if (!element) return;
      Object.assign(element, patch);
      markEdited(target);
    });

  /**
   * The card no longer matches the preset that dressed it.
   *
   * The name is kept rather than thrown away — “Custom, from Lower third” says
   * more than nothing, and re-picking the tile is how you get back. What is not
   * kept is the lit tile, because it would be claiming something untrue about
   * what is on screen.
   */
  const markEdited = (target: BookendScene) => {
    if (target.content.kind === "card" && target.content.preset && !target.content.preset.edited) {
      target.content.preset = { ...target.content.preset, edited: true };
    }
  };

  /** Patches one field of an element's style, leaving every other field unset. */
  const patchElementStyle = (id: string, patch: Partial<TextStyle>) =>
    update((target) => {
      if (target.content.kind !== "card") return;
      const element = target.content.elements.find((one) => one.id === id);
      if (element?.kind !== "text") return;
      element.style = { ...element.style, ...patch };
      markEdited(target);
    });

  const insert = (element: CardElement) => {
    update((target) => {
      if (target.content.kind === "card") target.content.elements.push(element);
    });
    onSelectElement(element.id);
  };

  // A second line is nearly always a subtitle under a title, so a card that
  // already has words starts the next one at the smaller of the two rather than
  // stacking two 88px headings on top of each other.
  const addText = () =>
    insert(newCardText("New text", elements.some((one) => one.kind === "text") ? "subtitle" : "title"));

  const moveElement = (id: string, by: number) =>
    update((target) => {
      if (target.content.kind !== "card") return;
      const list = target.content.elements;
      const from = list.findIndex((one) => one.id === id);
      const to = from + by;
      if (from < 0 || to < 0 || to >= list.length) return;
      [list[from], list[to]] = [list[to], list[from]];
    });

  const removeElement = (id: string) => {
    update((target) => {
      if (target.content.kind !== "card") return;
      target.content.elements = target.content.elements.filter((one) => one.id !== id);
    });
    if (selectedElementId === id) onSelectElement(null);
  };

  if (!scene) {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-[12px] leading-relaxed text-ink-300">
          No {which.toUpperCase()} section. Re-run processing to add one, or enable it below once it exists.
        </p>
      </div>
    );
  }

  // A section is a card or a full-bleed still, and nothing else.
  //
  // `text` and `video` used to be offered too. A text card is a card with a
  // title and no subtitle, and a card that plays a second video is footage,
  // which is what the BODY is. Neither is gone from the schema — a section
  // already set to one still renders, and the picker carries its current kind
  // along so it can be read and switched away from, but never switched back to.
  const kinds = offeredKinds.includes(scene.content.kind) ? offeredKinds : [...offeredKinds, scene.content.kind];

  /**
   * Two views, one column.
   *
   * Selecting a line slides its properties *over* the card's rather than beside
   * them. Beside would have meant a second column, and on a 1440px screen that
   * takes the frame from about 840px to 540 — a 16:9 preview drops from roughly
   * 470px tall to 300, which is where judging a 38px subtitle against footage
   * stops working. The preview is what the width is for.
   *
   * So the panel changes what it shows instead of getting wider, and a back
   * arrow returns. Nothing is selected until something is clicked: the list
   * used to quietly treat the first element as selected, which meant the frame
   * drew a box nobody had asked for.
   */
  const editing = selectedElementId ? elements.find((one) => one.id === selectedElementId) : undefined;

  return (
    <div className="flex flex-col gap-3">
      {editing ? (
        <div
          key={editing.id}
          className={`panel-slide flex flex-col gap-3 ${enabled ? "" : "pointer-events-none opacity-40"}`}
        >
          <button
            onClick={() => onSelectElement(null)}
            className="flex items-center gap-2 rounded-[6px] border border-[var(--edge)] px-2.5 py-1.5 text-left transition-colors hover:bg-bay-800"
          >
            <span className="text-[11px] text-ink-400">←</span>
            <span className="text-[9.5px] font-bold uppercase tracking-[0.1em] text-signal-ok">Selected</span>
            <span className="ml-auto truncate text-[11px] text-ink-100">{selectedSummary}</span>
          </button>

          {selected.kind === "text" ? (
            <>
              <Field label="Text">
                <textarea
                  className="input min-h-[54px] resize-y"
                  value={selected.text}
                  maxLength={500}
                  onChange={(event) => patchElement(selected.id, { text: event.target.value })}
                />
              </Field>

              <div className="flex flex-col gap-1.5">
                <span className="text-[11px] font-medium text-ink-300">Align the words</span>
                <div className="flex gap-1.5">
                  {(["left", "center", "right"] as const).map((align) => (
                    <button
                      key={align}
                      title={`Align ${align}`}
                      aria-label={`Align ${align}`}
                      aria-pressed={selected.align === align}
                      data-selected={selected.align === align}
                      onClick={() => patchElement(selected.id, { align })}
                      className="h-[30px] flex-1 rounded-[5px] border border-[var(--edge)] text-[11px] capitalize text-ink-300 transition-colors hover:bg-bay-800 hover:text-ink-100 data-[selected=true]:border-ember-500/50 data-[selected=true]:bg-bay-750 data-[selected=true]:text-ink-100"
                    >
                      {align}
                    </button>
                  ))}
                </div>
              </div>
            </>
          ) : null}

          {selected.kind === "image" ? (
            <>
              <BookendImage
                projectId={projectId}
                assetKey={selected.assetKey}
                onAssetKey={(assetKey) => patchElement(selected.id, { assetKey })}
              />
              <Field label="Fit">
                <Select
                  value={selected.fit}
                  options={asOptions(["contain", "cover"])}
                  onChange={(fit) => patchElement(selected.id, { fit })}
                />
              </Field>
              <Slider
                label="Corner radius"
                value={selected.radius}
                min={0}
                max={200}
                step={2}
                suffix="px"
                onChange={(radius) => patchElement(selected.id, { radius })}
              />
            </>
          ) : null}

          {selected.kind === "shape" ? (
            <>
              <Field label="Shape">
                <Select
                  value={selected.shape}
                  options={asOptions([...cardShapes])}
                  onChange={(shape) => patchElement(selected.id, { shape })}
                />
              </Field>
              {selected.shape === "rect" || selected.shape === "ellipse" ? (
                <ColorField
                  label="Fill"
                  value={selected.fill}
                  onChange={(fill) => patchElement(selected.id, { fill })}
                />
              ) : null}
              <ColorField
                label={selected.shape === "rect" || selected.shape === "ellipse" ? "Outline" : "Colour"}
                value={selected.stroke}
                onChange={(stroke) => patchElement(selected.id, { stroke })}
              />
              <Slider
                label={selected.shape === "rect" || selected.shape === "ellipse" ? "Outline" : "Thickness"}
                value={selected.strokeWidth}
                min={0}
                max={60}
                step={1}
                suffix="px"
                onChange={(strokeWidth) => patchElement(selected.id, { strokeWidth })}
              />
              {selected.shape === "rect" ? (
                <Slider
                  label="Corner radius"
                  value={selected.radius}
                  min={0}
                  max={200}
                  step={2}
                  suffix="px"
                  onChange={(radius) => patchElement(selected.id, { radius })}
                />
              ) : null}
            </>
          ) : null}

          {/* After the per-kind properties, because a shadow is about how an
              element sits on the card rather than what it is. Every kind gets
              it from one place: the property it turns into — text-shadow,
              box-shadow or a drop-shadow filter — is decided by the kind and
              the fit, never here. */}
          <Group title="Shadow">
            <div className="flex flex-wrap gap-1">
              {SHADOW_CHOICES.map(({ key, label }) => {
                const active = key === "none" ? !selected.shadow : matchShadowPreset(selected.shadow) === key;
                return (
                  <button
                    key={key}
                    type="button"
                    data-selected={active}
                    className="rounded-[5px] border border-[var(--edge)] px-2.5 py-1 text-[11.5px] text-ink-300 transition-colors hover:bg-bay-800 data-[selected=true]:border-ember-500/50 data-[selected=true]:text-ember-600"
                    onClick={() =>
                      patchElement(selected.id, {
                        shadow: key === "none" ? undefined : { ...cardShadowPresets[key] },
                      })
                    }
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            {/* Only once there is a shadow to tune. Four dials under a row
                that says "None" would be four dials doing nothing. */}
            {selected.shadow ? (
              <>
                <Slider
                  label="Across"
                  value={selected.shadow.x}
                  min={-60}
                  max={60}
                  step={1}
                  suffix="px"
                  onChange={(x) => patchElement(selected.id, { shadow: { ...selected.shadow!, x } })}
                />
                <Slider
                  label="Down"
                  value={selected.shadow.y}
                  min={-60}
                  max={60}
                  step={1}
                  suffix="px"
                  onChange={(y) => patchElement(selected.id, { shadow: { ...selected.shadow!, y } })}
                />
                <Slider
                  label="Blur"
                  value={selected.shadow.blur}
                  min={0}
                  max={120}
                  step={1}
                  suffix="px"
                  onChange={(blur) => patchElement(selected.id, { shadow: { ...selected.shadow!, blur } })}
                />
                <Slider
                  label="Strength"
                  value={selected.shadow.opacity}
                  min={0}
                  max={1}
                  step={0.01}
                  onChange={(opacity) => patchElement(selected.id, { shadow: { ...selected.shadow!, opacity } })}
                />
                <ColorField
                  label="Colour"
                  value={selected.shadow.color}
                  onChange={(color) => patchElement(selected.id, { shadow: { ...selected.shadow!, color } })}
                />
              </>
            ) : null}
          </Group>

          {selected.kind === "text" ? (
            <Group title="Words">
              {selected.kind === "text" ? (
                <TextStyleControls
                  which={selected.role}
                  style={selected.style}
                  onChange={(patch) => patchElementStyle(selected.id, patch)}
                  projectId={projectId}
                  fonts={fonts}
                  onAddFont={onAddFont}
                />
              ) : null}
            </Group>
          ) : null}

          <Group title="Place" note={selected.anchor === "free" ? "placed by hand" : "pinned"}>
            <AnchorGrid anchor={selected.anchor} onChange={(anchor) => patchElement(selected.id, { anchor })} />
            {selected.kind !== "text" ? (
              <Slider
                label="Opacity"
                value={selected.opacity}
                min={0}
                max={1}
                step={0.05}
                onChange={(opacity) => patchElement(selected.id, { opacity })}
              />
            ) : null}
          </Group>

          <Group title="Motion" note={`${selected.motion.entry.type} · this line only`}>
            <MotionControls motion={selected.motion} onChange={(motion) => patchElement(selected.id, { motion })} />
          </Group>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {/* Outer first: the section holds the card, the card holds the elements.
          The panel now reads in the order the thing itself is nested, which is
          the whole of the fix — every control below is the one that was already
          here, moved into the level it belongs to. */}
          <Band
            title="Section"
            summary={`${enabled ? "on" : "off"} · ${scene.duration.toFixed(1)}s${
              scene.script.trim() ? " · narrated" : ""
            }`}
            collapsible
            // Folded once the section is on, because then it is a set of
            // answered questions and the preset grid should be the first thing
            // on the panel. Open while it is off, because the switch that turns
            // it back on lives in here — hiding that behind a fold hides the one
            // control someone looking at a dimmed panel is reaching for.
            startOpen={!enabled}
          >
            <Toggle
              label={`${which.toUpperCase()} enabled`}
              checked={enabled}
              hint={`The ${which.toUpperCase()} section is optional and independent of your recording.`}
              onChange={(value) => update((target) => void (target.enabled = value))}
            />

            <div className={enabled ? "flex flex-col gap-3" : "pointer-events-none flex flex-col gap-3 opacity-40"}>
              {/* "Duration" named a unit. This names the question someone is
              actually asking, which is how long the card is up before the
              video moves on. */}
              <Slider
                label="How long it stays"
                value={scene.duration}
                min={1}
                max={20}
                step={0.5}
                suffix="s"
                onChange={(value) => update((target) => void (target.duration = value))}
              />

              <Field label="Narration" hint="Leave blank for a silent card.">
                <textarea
                  className="input min-h-[60px] resize-y"
                  value={scene.script}
                  maxLength={4000}
                  onChange={(event) => update((target) => void (target.script = event.target.value))}
                />
              </Field>

              <SectionVoice
                projectId={projectId}
                which={which}
                voiceId={scene.voiceId}
                onVoiceId={(value) => update((target) => void (target.voiceId = value))}
                onRebuilt={onRebuilt}
                // A card with no words is a silent card, which is a legitimate choice.
                disabled={scene.script.trim().length === 0}
                voice={
                  hasVoice
                    ? {
                        enabled: scene.voiceEnabled !== false,
                        // Both halves, together. Saving a timeline does not
                        // re-run the builder, so setting only the section flag
                        // would leave the track audible until something else
                        // rebuilt the project.
                        onEnabled: (value) =>
                          commit((draft) => {
                            const target = draft.sections[which];
                            if (target) target.voiceEnabled = value;
                            const track = draft.audio.find((candidate) => candidate.role === `${which}-voice`);
                            if (track) track.enabled = value;
                            return draft;
                          }),
                        offset: scene.voiceOffset ?? 0,
                        onOffset: (value) =>
                          commit((draft) => {
                            applyBookendVoiceOffset(draft, which, value);
                            return draft;
                          }),
                      }
                    : undefined
                }
              />
            </div>
          </Band>

          <div className={enabled ? "flex flex-col gap-3" : "pointer-events-none flex flex-col gap-3 opacity-40"}>
            {/* Collapsible but open: the preset grid is the tall part of this
            panel, and folding it once a preset is chosen is worth offering —
            but it is also the first thing someone wants when they open a card,
            so it is not folded for them. */}
            <Band title="Card" summary={cardSummary} collapsible>
              <Field label="Type">
                <Select
                  value={scene.content.kind}
                  options={asOptions(kinds)}
                  onChange={(kind) =>
                    update((target) => {
                      // Re-picking the kind it already is, which is the only way a
                      // carried-along legacy kind can be chosen. Rebuilding the
                      // content would throw away what is in it for no change.
                      if (kind === target.content.kind) return;
                      // Otherwise switching replaces the content union with a valid
                      // default for the new kind rather than leaving mismatched
                      // fields behind.
                      if (kind === "card") target.content = cardContent("");
                      // No assetKey: the picture is chosen below, and until it is
                      // there is honestly nothing to point at.
                      else target.content = { kind: "image", fit: "cover", kenBurns: true };
                    })
                  }
                />
              </Field>

              {scene.content.kind === "card" ? (
                <>
                  {/* What is on the card comes before how it is dressed.

                      A preset restyles the elements that exist; it never adds
                      or removes any. So the order people actually work in is
                      put the words on, then dress them — and this section was
                      last, below a palette and a colour picker, which is why
                      Insert was hard to find at all. */}
                  <div className="flex items-center justify-between">
                    <Eyebrow>On the card</Eyebrow>
                    <InsertMenu
                      onInsert={(what) =>
                        what === "text"
                          ? addText()
                          : what === "image"
                            ? insert(newCardImage())
                            : insert(newCardShape(what))
                      }
                    />
                  </div>
                  {elements.length === 0 ? (
                    <p className="text-[11px] leading-relaxed text-ink-400">
                      Nothing on this card yet. Insert something and drag it wherever it belongs.
                    </p>
                  ) : (
                    <div className="flex flex-col gap-1">
                      {elements.map((element, index) => (
                        <div
                          key={element.id}
                          data-selected={element.id === selected?.id}
                          className="group flex items-center gap-1.5 rounded-[5px] border border-transparent px-2 py-1.5 transition-colors hover:bg-bay-800 data-[selected=true]:border-ember-500/40 data-[selected=true]:bg-bay-750"
                        >
                          <span className="shrink-0 text-[11px] text-ink-400">{elementIcon(element)}</span>
                          <button
                            onClick={() => onSelectElement(element.id)}
                            className="min-w-0 flex-1 truncate text-left text-[12px] text-ink-200"
                            style={{ fontFamily: element.kind === "text" ? element.style?.fontFamily : undefined }}
                          >
                            {elementName(element)}
                          </button>
                          {/* Paint order is array order, so these are the layer
              controls: what is later in the list is drawn on top. */}
                          <button
                            onClick={() => moveElement(element.id, -1)}
                            disabled={index === 0}
                            title="Send backwards"
                            aria-label="Send backwards"
                            className="shrink-0 px-1 text-[10px] text-ink-400 opacity-0 transition-opacity hover:text-ink-100 disabled:opacity-20 group-hover:opacity-100"
                          >
                            ▲
                          </button>
                          <button
                            onClick={() => moveElement(element.id, 1)}
                            disabled={index === elements.length - 1}
                            title="Bring forwards"
                            aria-label="Bring forwards"
                            className="shrink-0 px-1 text-[10px] text-ink-400 opacity-0 transition-opacity hover:text-ink-100 disabled:opacity-20 group-hover:opacity-100"
                          >
                            ▼
                          </button>
                          <button
                            onClick={() => removeElement(element.id)}
                            title="Delete"
                            aria-label="Delete"
                            className="shrink-0 rounded-[4px] px-1.5 text-[13px] leading-none text-ink-400 opacity-0 transition-opacity hover:text-ink-100 group-hover:opacity-100"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* The preset sits above the background because applying one
                  *writes* the background. With it the other way round you would
                  choose a colour and have it overwritten by a control further
                  down the panel. */}
                  {/* Brand fonts and text colour, on a card that already exists.
                      `applyBrandToCard` only ever ran when a card was created,
                      so changing the kit's faces afterwards reached nothing —
                      a card made when the kit said Inter kept Inter for ever.
                      Offered rather than applied on rebuild, for the same
                      reason the background is: what is on a card may have been
                      chosen deliberately, and a rebuild is not the moment to
                      overrule that. */}
                  {brand && isBrandConfigured(brand) && scene.content.kind === "card" ? (
                    <button
                      className="btn btn-secondary btn-sm self-start"
                      title={`Set this card's faces and text colour from the brand kit — ${brand.titleFont}`}
                      onClick={() =>
                        update((target) => {
                          if (target.content.kind === "card") {
                            target.content = applyBrandToCard(target.content, brand);
                          }
                        })
                      }
                    >
                      Use brand fonts
                    </button>
                  ) : null}

                  <PresetGallery
                    preset={scene.content.preset}
                    onPick={(id) =>
                      update((target) => {
                        if (target.content.kind === "card") target.content = applyCardPreset(target.content, id);
                      })
                    }
                  />

                  {/* Above the picker, for the same reason as in BODY: last in
                      the block put it on the scroll edge, where an offer that
                      saves real work goes unread. */}
                  <BackgroundSpread timeline={timeline} which={which} commit={commit} />

                  {/* The card's own background. It was filed under the section and
                  called "Style", which named the control rather than the thing
                  — what you are choosing is the background, and the three
                  styles are how it is made. */}
                  <BackdropControls
                    projectId={projectId}
                    brand={brand}
                    background={scene.background}
                    onChange={(background) =>
                      update((target) => {
                        target.background = background;
                        // A preset writes the background, so changing it by hand is
                        // a divergence like any other. Without this the gallery
                        // keeps a tile lit that no longer describes the card.
                        markEdited(target);
                      })
                    }
                  />
                </>
              ) : null}

              {scene.content.kind === "text" ? (
                <>
                  <p className="text-[11px] leading-relaxed text-ink-400">
                    This section is a plain text slide, a type that is no longer offered. Switch it to Card above to get
                    a title, a subtitle and their styling.
                  </p>
                  <Field label="Text">
                    <textarea
                      className="input min-h-[70px] resize-y"
                      value={scene.content.text}
                      maxLength={600}
                      onChange={(event) =>
                        update((target) => {
                          if (target.content.kind === "text") target.content.text = event.target.value || " ";
                        })
                      }
                    />
                  </Field>
                  <Field label="Animation">
                    <Select
                      value={scene.content.animation}
                      options={asOptions(["rise", "fade", "typewriter", "word-by-word"])}
                      onChange={(value) =>
                        update((target) => {
                          if (target.content.kind === "text") {
                            target.content.animation = value as typeof target.content.animation;
                          }
                        })
                      }
                    />
                  </Field>
                </>
              ) : null}

              {scene.content.kind === "image" ? (
                <>
                  <BookendImage
                    projectId={projectId}
                    assetKey={scene.content.assetKey}
                    onAssetKey={(assetKey) =>
                      update((target) => {
                        if (target.content.kind === "image") target.content.assetKey = assetKey;
                      })
                    }
                  />
                  <Field label="Fit">
                    <Select
                      value={scene.content.fit}
                      options={asOptions(["cover", "contain"])}
                      onChange={(value) =>
                        update((target) => {
                          if (target.content.kind === "image") target.content.fit = value as "cover" | "contain";
                        })
                      }
                    />
                  </Field>
                  <Toggle
                    label="Slow zoom"
                    checked={scene.content.kenBurns}
                    hint="Drifts in over the card's length so a still does not sit dead on screen."
                    onChange={(value) =>
                      update((target) => {
                        if (target.content.kind === "image") target.content.kenBurns = value;
                      })
                    }
                  />
                </>
              ) : null}
            </Band>
          </div>
        </div>
      )}
    </div>
  );
};

const TransitionEditor: React.FC<{
  label: string;
  transition: Transition | undefined;
  faces: BoundaryFaces;
  onChange: (next: Transition) => void;
}> = ({ label, transition, faces, onChange }) => {
  if (!transition) {
    return <p className="text-[12px] text-ink-400">{label} — enable both sections to configure this transition.</p>;
  }

  // Every edit writes the standard easing along with it; see `standardEasing`.
  const set = (next: Partial<Transition>) => onChange({ ...transition, ...next, easing: standardEasing });

  // A timeline written before the paces existed can hold any duration the old
  // slider allowed. Rather than round it into the nearest name and show a pace
  // the section is not running at, nothing is selected and the real value is
  // named underneath. Picking a pace snaps to it.
  const pace = transitionPaces.find((candidate) => Math.abs(candidate.seconds - transition.duration) < 0.001);

  return (
    <div className="flex flex-col gap-3 border-b border-[var(--edge)] pb-4 last:border-b-0 last:pb-0">
      <Eyebrow>{label}</Eyebrow>
      <TransitionPicker value={transition.type} faces={faces} onChange={(type) => set({ type })} />

      <div className="flex flex-col gap-1.5">
        <span className="text-[11px] font-medium text-ink-300">Pace</span>
        <div className="flex gap-1.5">
          {transitionPaces.map((candidate) => (
            <button
              key={candidate.name}
              title={`${candidate.seconds.toFixed(2)}s`}
              data-selected={candidate.name === pace?.name}
              onClick={() => set({ duration: candidate.seconds })}
              className="flex-1 rounded-[5px] border border-[var(--edge)] py-1 text-[11px] font-medium text-ink-300 transition-colors hover:bg-bay-800 hover:text-ink-100 data-[selected=true]:border-ember-500/50 data-[selected=true]:bg-bay-750 data-[selected=true]:text-ink-100"
            >
              {candidate.name}
            </button>
          ))}
        </div>
        {pace ? null : (
          <span className="tnum text-[10px] text-ink-400">
            Currently {transition.duration.toFixed(2)}s — pick a pace to change it.
          </span>
        )}
      </div>
    </div>
  );
};

const InspectorBase: React.FC<InspectorProps> = ({
  projectId,
  timeline,
  pane,
  commit,
  selectedActionId,
  onSelectAction,
  selectedFocusId,
  onSelectFocus,
  selectedElementId,
  onSelectElement,
  playheadSeconds,
  onSeekSeconds,
  onRebuilt,
  recording,
  autoCut,
  filmstrip,
}) => {
  // Fetched once here rather than in each control: three backdrop pickers want
  // the same four colours, and three requests for one answer is three chances
  // to disagree.
  const [brand, setBrand] = useState<BrandKit | null>(null);
  useEffect(() => {
    let cancelled = false;
    void api
      .settings()
      .then((view) => !cancelled && setBrand(view.brandKit))
      // No kit means no Brand row, which is the same as not having set one —
      // not worth an error on a panel about a card.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const body = timeline.sections.body;

  // The recording's own sound is a track like any other, so the controls read
  // and write the track rather than a second copy of the setting on the scene.
  const originalAudio = timeline.audio.find((track) => track.role === "original");
  /**
   * What the Narration band says while folded.
   *
   * Whether the body has actually been voiced, because that is the question
   * someone opening this band is asking — a script with no clips is the state
   * worth being told about without having to unfold anything to find out.
   */
  const voicedLines = timeline.audio.filter((track) => track.role === "body-voice").length;
  const narrationSummary = voicedLines
    ? `${voicedLines} ${voicedLines === 1 ? "line" : "lines"} voiced`
    : "not voiced yet";
  const hasNarration = timeline.audio.some((track) => track.role === "body-voice");
  const selectedAction = body.actions.find((action) => action.id === selectedActionId) ?? null;
  /** The effect this action put on the layer, if it still has one. */
  const ownEffect = selectedAction ? body.focus.find((effect) => effect.actionId === selectedAction.id) : undefined;
  // An effect chosen on the timeline wins; otherwise fall back to whatever
  // the selected action produced, which is how this pane always worked.
  const selectedFocus =
    body.focus.find((effect) => effect.id === selectedFocusId) ??
    body.focus.find((effect) => effect.actionId === selectedActionId) ??
    null;

  // What each boundary actually joins, so the tiles preview this project rather
  // than a stock example. The card sides fall back to the body's own backdrop
  // when the section is missing — a transition can be configured before the
  // card it leads into exists.
  const cardTitle = (scene: typeof timeline.sections.head) =>
    scene ? cardLabel(scene.content) || undefined : undefined;

  const headToBodyFaces: BoundaryFaces = {
    from: cardFace(timeline.sections.head?.background ?? body.background, cardTitle(timeline.sections.head)),
    to: recordingFace(filmstrip ?? null, 0, body.duration),
  };
  const bodyToEndFaces: BoundaryFaces = {
    from: recordingFace(filmstrip ?? null, Math.max(0, body.duration - 1), body.duration),
    to: cardFace(timeline.sections.end?.background ?? body.background, cardTitle(timeline.sections.end)),
  };

  /**
   * Promotes a detected action into an effect.
   *
   * Seeded with the action's own region when it has one, and left un-aimed when
   * it does not — an action detected from a scene change knows *when* something
   * happened and not *where*, and guessing a rectangle for it is the thing that
   * made every mode look like it pointed at the middle of the screen.
   */
  const addFocusForAction = (actionId: string, timestamp: number, target: Rect | undefined) => {
    const id = `focus-${actionId}`;
    commit((draft) => {
      if (draft.sections.body.focus.some((effect) => effect.id === id)) return draft;
      const bodyDuration = draft.sections.body.duration;
      const start = Math.min(Math.max(0, timestamp - 0.3), Math.max(0, bodyDuration - 0.2));
      draft.sections.body.focus.push({
        id,
        actionId,
        mode: "zoom",
        blur: 18,
        start: Number(start.toFixed(3)),
        duration: Number(Math.min(1.5, bodyDuration - start).toFixed(3)),
        target,
        zoom: 1.35,
        dim: 0,
        cursor: true,
        easeIn: 0.5,
        easeOut: 0.5,
        easing: "ease-in-out",
        click: false,
      });
      return draft;
    });
    onSelectFocus(id);
  };

  /**
   * Removes a detected action, and whatever it put on the layer.
   *
   * Nothing is cut from the recording — an action is a marker saying something
   * happened here, and detection is a guess that is sometimes wrong.
   */
  const deleteAction = (actionId: string) => {
    commit((draft) => {
      draft.sections.body.actions = draft.sections.body.actions.filter((action) => action.id !== actionId);
      draft.sections.body.focus = draft.sections.body.focus.filter((effect) => effect.actionId !== actionId);
      return draft;
    });
    onSelectAction(null);
    onSelectFocus(null);
  };

  /** One edit to one effect. Every control in the panel goes through it. */
  const patchFocus = (id: string, mutate: (effect: FocusEffect) => void) =>
    commit((draft) => {
      const effect = draft.sections.body.focus.find((candidate) => candidate.id === id);
      if (effect) mutate(effect);
      return draft;
    });

  const moveFocus = (id: string, start: number, duration: number) =>
    commit((draft) => {
      const effect = draft.sections.body.focus.find((candidate) => candidate.id === id);
      if (!effect) return draft;
      // Same floor the timeline drag enforces, so typing a number and dragging
      // an edge cannot disagree about what the shortest effect is.
      effect.start = Math.max(0, Number(start.toFixed(3)));
      effect.duration = Math.max(0.2, Number(duration.toFixed(3)));
      return draft;
    });

  /**
   * A new effect where the playhead is.
   *
   * Deliberately not attached to an action: the layer is the user's now, and an
   * effect that exists because someone wanted one there is the case the old
   * derived-from-actions model had no way to express at all.
   */
  const addFocusAtPlayhead = () => {
    const id = `focus-manual-${Date.now().toString(36)}`;
    commit((draft) => {
      const bodyDuration = draft.sections.body.duration;
      const start = Math.min(Math.max(0, playheadSeconds), Math.max(0, bodyDuration - 0.2));
      draft.sections.body.focus.push({
        id,
        mode: "zoom",
        blur: 18,
        start: Number(start.toFixed(3)),
        duration: Number(Math.min(2, bodyDuration - start).toFixed(3)),
        // No target. "Not aimed yet" is the honest starting state — the
        // placeholder rectangle this used to invent is why every mode looked
        // like it pointed at the middle of the screen.
        zoom: 1.35,
        dim: 0,
        cursor: false,
        easeIn: 0.5,
        easeOut: 0.5,
        easing: "ease-in-out",
        click: false,
      });
      return draft;
    });
    onSelectFocus(id);
  };

  /** An uploaded face has to travel with the timeline; the renderer gets nothing else. */
  const addFont = (font: ProjectFont) =>
    commit((draft) => {
      if (!draft.fonts.some((existing) => existing.family === font.family)) draft.fonts.push(font);
      return draft;
    });

  return (
    <div className="flex flex-col gap-4 p-4">
      {pane === "HEAD" ? (
        <BookendPane
          which="head"
          scene={timeline.sections.head}
          commit={commit}
          projectId={projectId}
          onRebuilt={onRebuilt}
          selectedElementId={selectedElementId}
          onSelectElement={onSelectElement}
          fonts={timeline.fonts}
          onAddFont={addFont}
          hasVoice={timeline.audio.some((track) => track.role === "head-voice" && track.assetKey)}
          timeline={timeline}
          brand={brand}
        />
      ) : null}
      {pane === "END" ? (
        <BookendPane
          which="end"
          scene={timeline.sections.end}
          commit={commit}
          projectId={projectId}
          onRebuilt={onRebuilt}
          selectedElementId={selectedElementId}
          onSelectElement={onSelectElement}
          fonts={timeline.fonts}
          onAddFont={addFont}
          hasVoice={timeline.audio.some((track) => track.role === "end-voice" && track.assetKey)}
          timeline={timeline}
          brand={brand}
        />
      ) : null}

      {pane === "BODY" ? (
        <div className="flex flex-col gap-3">
          <Band
            title="Recording"
            summary={
              recording?.durationSeconds
                ? `${timecode(recording.durationSeconds)} · ${narrationSummary}`
                : narrationSummary
            }
            collapsible
            // Open. It was folded when it held only facts about the file, and
            // narration — which is what someone opens the BODY to change — has
            // moved in since. The dimensions moved out of the summary for the
            // same reason: what is worth reading at a glance is whether the
            // voice exists, not how many pixels wide the source is.
            startOpen
          >
            <p className="text-[11px] leading-relaxed text-ink-400">
              Your recording. It is always present and cannot be removed — everything else is layered over it.
            </p>

            {recording ? (
              <dl className="grid grid-cols-3 gap-2 rounded-[6px] border border-[var(--edge)] bg-bay-850 p-2.5">
                <div>
                  <dt className="text-[10px] text-ink-400">Length</dt>
                  <dd className="tnum mt-0.5 text-[12px] font-semibold text-ink-100">
                    {recording.durationSeconds ? timecode(recording.durationSeconds) : "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-[10px] text-ink-400">Source</dt>
                  <dd className="tnum mt-0.5 text-[12px] font-semibold text-ink-100">
                    {recording.width && recording.height ? `${recording.width}×${recording.height}` : "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-[10px] text-ink-400">Actions</dt>
                  <dd className="tnum mt-0.5 text-[12px] font-semibold text-ink-100">{body.actions.length}</dd>
                </div>
              </dl>
            ) : null}

            <Field label="Fit">
              <Select
                value={body.fit}
                options={asOptions(["contain", "cover"])}
                onChange={(value) =>
                  commit((draft) => {
                    draft.sections.body.fit = value as "contain" | "cover";
                    return draft;
                  })
                }
              />
            </Field>

            {originalAudio ? (
              <>
                <Toggle
                  label="Keep original audio"
                  checked={originalAudio.enabled}
                  disabled={autoCut?.busy}
                  hint={
                    hasNarration
                      ? originalAudio.enabled
                        ? "Playing instead of the narration. Turn it off to hear the narration."
                        : "The narration plays instead. Turn this on to hear your recording rather than the narration."
                      : "The sound from your recording."
                  }
                  onChange={(value) => {
                    commit((draft) => {
                      setOriginalAudio(draft, value);
                      return draft;
                    });
                    autoCut?.onVoiceChanged?.(value);
                  }}
                />

                {originalAudio.enabled ? (
                  <Slider
                    label="Original audio level"
                    value={originalAudio.volume}
                    min={0}
                    max={1}
                    step={0.05}
                    // Locked with the toggle above while a re-cut rebuilds the timeline.
                    disabled={autoCut?.busy}
                    onChange={(value) =>
                      commit((draft) => {
                        const track = draft.audio.find((entry) => entry.role === "original");
                        if (track) track.volume = value;
                        return draft;
                      })
                    }
                  />
                ) : null}
              </>
            ) : (
              <p className="text-[11px] leading-relaxed text-ink-400">
                This recording has no sound of its own, so there is no original audio layer.
              </p>
            )}

            {/* Narration sits inside the recording rather than in a section of
                its own. It is that recording's own soundtrack — the same thing
                the original audio above it is — and a separate band put a
                heading between two halves of one idea. */}
            <SectionVoice
              projectId={projectId}
              which="body"
              hideNarrator
              voiceId={timeline.sections.body.voiceId}
              onVoiceId={(value) =>
                commit((draft) => {
                  draft.sections.body.voiceId = value;
                  return draft;
                })
              }
              onRebuilt={onRebuilt}
            />

            <div className="rounded-[6px] border border-[var(--edge)] bg-bay-900 p-3">
              <Eyebrow>Narration</Eyebrow>
              <p className="tnum mt-1.5 text-[12px] text-ink-200">
                {timeline.audio.filter((track) => track.role === "body-voice").length} clips
              </p>
              <p className="mt-1 text-[11px] leading-relaxed text-ink-400">
                Edit the wording in the script on the left, then generate the voice; the voice lane updates when the new
                audio is ready.
              </p>
            </div>
          </Band>

          <Band title="Background" summary={body.frame?.enabled ? "framed" : "fills the screen"} collapsible>
            <div className="rounded-[6px] border border-[var(--edge)] bg-bay-850 p-2.5">
              <Toggle
                label="Background"
                checked={body.frame?.enabled ?? false}
                hint="Sit the video on a background instead of filling the frame."
                onChange={(value) =>
                  commit((draft) => {
                    draft.sections.body.frame = { ...defaultFrame, ...draft.sections.body.frame, enabled: value };
                    return draft;
                  })
                }
              />

              {body.frame?.enabled ? (
                <div className="mt-3 flex flex-col gap-3">
                  {/* Directly under the toggle, above the picker it is about.
                      It used to sit between the picker and the inset slider,
                      which is the middle of a long scrolling column — so the
                      one control that saves setting a background three times
                      by hand spent its life half off the bottom of the panel.
                      Here it is the first thing in the section and cannot be
                      scrolled past without being seen. */}
                  <BackgroundSpread timeline={timeline} which="body" commit={commit} />

                  <BackdropControls
                    projectId={projectId}
                    brand={brand}
                    background={body.background}
                    onChange={(next) =>
                      commit((draft) => {
                        draft.sections.body.background = next;
                        return draft;
                      })
                    }
                  />

                  <Slider
                    label="Inset"
                    value={Math.round((body.frame.padding ?? 0) * 100)}
                    min={0}
                    max={20}
                    step={1}
                    suffix="%"
                    onChange={(value) =>
                      commit((draft) => {
                        draft.sections.body.frame = {
                          ...defaultFrame,
                          ...draft.sections.body.frame,
                          enabled: true,
                          padding: value / 100,
                        };
                        return draft;
                      })
                    }
                  />

                  <Slider
                    label="Corner radius"
                    value={body.frame.radius}
                    min={0}
                    max={80}
                    step={2}
                    onChange={(radius) =>
                      commit((draft) => {
                        draft.sections.body.frame = {
                          ...defaultFrame,
                          ...draft.sections.body.frame,
                          enabled: true,
                          radius,
                        };
                        return draft;
                      })
                    }
                  />

                  <Field label="Shadow">
                    <Select
                      value={body.frame.shadow}
                      options={[
                        { value: "none", label: "None" },
                        { value: "soft", label: "Soft" },
                        { value: "strong", label: "Strong" },
                      ]}
                      onChange={(value) =>
                        commit((draft) => {
                          draft.sections.body.frame = {
                            ...defaultFrame,
                            ...draft.sections.body.frame,
                            enabled: true,
                            shadow: value as "none" | "soft" | "strong",
                          };
                          return draft;
                        })
                      }
                    />
                  </Field>
                </div>
              ) : null}
            </div>
          </Band>
        </div>
      ) : null}

      {pane === "TRANSITIONS" ? (
        <div className="flex flex-col gap-4">
          <TransitionEditor
            label="HEAD → BODY"
            faces={headToBodyFaces}
            transition={timeline.transitions.headToBody}
            onChange={(next) =>
              commit((draft) => {
                draft.transitions.headToBody = next;
                return draft;
              })
            }
          />
          <TransitionEditor
            label="BODY → END"
            faces={bodyToEndFaces}
            transition={timeline.transitions.bodyToEnd}
            onChange={(next) =>
              commit((draft) => {
                draft.transitions.bodyToEnd = next;
                return draft;
              })
            }
          />
          <p className="text-[11px] leading-relaxed text-ink-400">
            A transition overlaps the two sections it joins, so lengthening one shortens the finished video.
          </p>
        </div>
      ) : null}

      {pane === "REWRITE" ? <RewritePane projectId={projectId} onRebuilt={onRebuilt} autoCut={autoCut} /> : null}

      {pane === "CAPTIONS" ? (
        <div className="flex flex-col gap-4">
          <Toggle
            label="Show captions"
            checked={timeline.captions.enabled}
            hint={`${timeline.captions.cues.length} cues from the transcript.`}
            onChange={(value) =>
              commit((draft) => {
                draft.captions.enabled = value;
                return draft;
              })
            }
          />
          <div
            className={
              timeline.captions.enabled ? "flex flex-col gap-4" : "pointer-events-none flex flex-col gap-4 opacity-40"
            }
          >
            <Field label="Position">
              <Select
                value={timeline.captions.style.position}
                options={asOptions(["bottom", "center", "top"])}
                onChange={(value) =>
                  commit((draft) => {
                    draft.captions.style.position = value as "bottom" | "center" | "top";
                    return draft;
                  })
                }
              />
            </Field>
            <Slider
              label="Size"
              value={timeline.captions.style.fontSize}
              min={20}
              max={90}
              step={2}
              suffix="px"
              onChange={(value) =>
                commit((draft) => {
                  draft.captions.style.fontSize = value;
                  return draft;
                })
              }
            />
            <Toggle
              label="Highlight the spoken word"
              checked={timeline.captions.style.wordHighlight}
              onChange={(value) =>
                commit((draft) => {
                  draft.captions.style.wordHighlight = value;
                  return draft;
                })
              }
            />
            <Toggle
              label="Uppercase"
              checked={timeline.captions.style.uppercase}
              onChange={(value) =>
                commit((draft) => {
                  draft.captions.style.uppercase = value;
                  return draft;
                })
              }
            />
          </div>
        </div>
      ) : null}

      {pane === "ACTIONS" ? (
        <div className="flex flex-col gap-3">
          {/* The layer is editable now, so an empty list is a starting point
              rather than a verdict. */}
          <div className="flex items-center justify-between gap-2">
            <Eyebrow>Effects · {body.focus.length}</Eyebrow>
            <button
              className="btn btn-secondary btn-sm"
              title="Add an effect at the playhead, then drag it on the timeline"
              onClick={addFocusAtPlayhead}
            >
              + Add effect
            </button>
          </div>

          {body.actions.length === 0 ? (
            <p className="text-[12px] leading-relaxed text-ink-400">
              Nothing was detected in this recording. Add an effect above to zoom or highlight anyway.
            </p>
          ) : null}

          <div className="flex max-h-[190px] flex-col gap-1 overflow-y-auto">
            {body.actions.map((action) => (
              <button
                key={action.id}
                onClick={() => {
                  onSelectAction(action.id);
                  onSeekSeconds(action.timestamp);
                }}
                data-selected={action.id === selectedActionId}
                className="rail-item"
              >
                <span className="flex min-w-0 flex-col">
                  <span className="truncate">{action.label ?? action.type}</span>
                  <span className="tnum text-[10px] text-ink-400">
                    {timecode(action.timestamp)} · {action.type}
                  </span>
                </span>
                {/* What this action does on the layer, on the row. Detected
                    actions used to say only that they existed, so changing what
                    one does meant finding its clip on the timeline first. */}
                {(() => {
                  const own = body.focus.find((effect) => effect.actionId === action.id);
                  if (own) {
                    return (
                      <span
                        className="shrink-0 rounded-full border border-ember-600/50 bg-ember-600/15 px-2 py-px text-[10px] font-semibold text-ember-400"
                        title="Select this effect to change it"
                      >
                        {EFFECT_LABELS[own.mode]}
                        {own.target ? "" : " · not aimed"}
                      </span>
                    );
                  }
                  return (
                    <span
                      role="button"
                      tabIndex={0}
                      title="Add an effect aimed at this action"
                      onClick={(event) => {
                        event.stopPropagation();
                        addFocusForAction(action.id, action.timestamp, action.target);
                      }}
                      onKeyDown={(event) => {
                        if (event.key !== "Enter" && event.key !== " ") return;
                        event.stopPropagation();
                        addFocusForAction(action.id, action.timestamp, action.target);
                      }}
                      className="shrink-0 cursor-pointer rounded-full border border-[var(--edge-strong)] px-2 py-px text-[10px] font-semibold text-ink-400 hover:border-ember-500/60 hover:text-ember-400"
                    >
                      + Effect
                    </span>
                  );
                })()}
              </button>
            ))}
          </div>

          {selectedAction ? (
            <div className="flex flex-col gap-4 rounded-[8px] border border-[var(--edge)] bg-bay-900 p-3">
              <div>
                <Eyebrow>Selected action</Eyebrow>
                <p className="tnum mt-1 text-[13px] font-semibold text-ink-100">{timecode(selectedAction.timestamp)}</p>
              </div>

              <Field label="Label">
                <input
                  className="input"
                  value={selectedAction.label ?? ""}
                  maxLength={280}
                  onChange={(event) =>
                    commit((draft) => {
                      const action = draft.sections.body.actions.find(
                        (candidate) => candidate.id === selectedAction.id,
                      );
                      if (action) action.label = event.target.value || undefined;
                      return draft;
                    })
                  }
                />
              </Field>

              {/* No box control here any more.
                  A region belongs to an effect, not to an action: the action
                  records that something happened and roughly where, and the
                  effect is what points at it. "Place a focus box" wrote the
                  same hardcoded centre rectangle that made every mode look like
                  it ignored its target, and "Clear box" existed only to undo
                  it — those are the effect's Re-aim and Delete now. What is
                  left is what the recording actually knows. */}
              <p className="tnum text-[11px] leading-relaxed text-ink-400">
                {selectedAction.target
                  ? `Detected around ${(selectedAction.target.x * 100).toFixed(0)},${(selectedAction.target.y * 100).toFixed(0)} — an effect added here starts there.`
                  : "No on-screen position was detected, so an effect added here starts un-aimed."}
              </p>

              {/* Detection is a guess, and a wrong marker was permanent until
                  now. Deleting takes the effect with it: the effect exists
                  because the action does, and leaving an orphan pointing at a
                  moment that is no longer marked helps nobody. `Delete effect`
                  is the other direction, and keeps the marker. */}
              <button
                className="btn btn-secondary btn-sm self-start"
                title={
                  ownEffect
                    ? "Remove this action and the effect on it"
                    : "Remove this action. Nothing is cut from the recording."
                }
                onClick={() => deleteAction(selectedAction.id)}
              >
                {ownEffect ? "Delete action and effect" : "Delete action"}
              </button>
            </div>
          ) : null}
          {selectedFocus ? (
            <div className="flex flex-col gap-4 rounded-[8px] border border-[var(--edge)] bg-bay-900 p-3">
              <Eyebrow>Selected effect</Eyebrow>
              <Field label="Focus effect">
                <Select
                  value={selectedFocus.mode}
                  options={asOptions(focusModes)}
                  onChange={(value) =>
                    commit((draft) => {
                      const effect = draft.sections.body.focus.find((candidate) => candidate.id === selectedFocus.id);
                      if (effect) effect.mode = value as typeof effect.mode;
                      return draft;
                    })
                  }
                />
              </Field>
              {/* Only the options this effect actually has. A panel that shows
                  a zoom amount on a highlight is the other half of why these
                  were hard to tell apart. */}
              {selectedFocus.mode === "zoom" ? (
                <>
                  <Slider
                    label="Zoom"
                    value={selectedFocus.zoom}
                    min={1}
                    max={2.5}
                    step={0.05}
                    suffix="×"
                    onChange={(value) => patchFocus(selectedFocus.id, (effect) => void (effect.zoom = value))}
                  />
                  <Toggle
                    label="Move the cursor"
                    checked={selectedFocus.cursor}
                    hint="Draws a pointer travelling to the region as the zoom lands."
                    onChange={(value) => patchFocus(selectedFocus.id, (effect) => void (effect.cursor = value))}
                  />
                </>
              ) : null}

              {selectedFocus.mode === "highlight" || selectedFocus.mode === "callout" ? (
                <Slider
                  label="Dim surroundings"
                  value={Math.round(selectedFocus.dim * 100)}
                  min={0}
                  max={80}
                  step={5}
                  suffix="%"
                  onChange={(value) => patchFocus(selectedFocus.id, (effect) => void (effect.dim = value / 100))}
                />
              ) : null}

              {selectedFocus.mode === "blur" ? (
                <>
                  <Slider
                    label="Blur"
                    value={selectedFocus.blur}
                    min={4}
                    max={80}
                    step={2}
                    suffix="px"
                    onChange={(value) => patchFocus(selectedFocus.id, (effect) => void (effect.blur = value))}
                  />
                  <p className="text-[11px] leading-relaxed text-ink-400">
                    Full strength from its first frame — unlike the other effects, this one does not ease in, because a
                    blur that fades up shows what it is covering. Check the result: a light blur over small text can
                    still be read.
                  </p>
                </>
              ) : null}

              {selectedFocus.mode === "callout" ? (
                <Field label="Label">
                  <input
                    className="input"
                    value={selectedFocus.label ?? ""}
                    maxLength={280}
                    onChange={(event) =>
                      patchFocus(selectedFocus.id, (effect) => void (effect.label = event.target.value))
                    }
                  />
                </Field>
              ) : null}

              <Field label="Region">
                {selectedFocus.target ? (
                  <div className="flex items-center gap-2">
                    <span className="tnum flex-1 text-[11px] text-ink-400">
                      {(selectedFocus.target.x * 100).toFixed(0)},{(selectedFocus.target.y * 100).toFixed(0)} ·{" "}
                      {(selectedFocus.target.width * 100).toFixed(0)}×{(selectedFocus.target.height * 100).toFixed(0)}
                    </span>
                    <button
                      className="btn btn-secondary btn-sm"
                      title="Clear the region and draw a new one on the frame"
                      onClick={() => patchFocus(selectedFocus.id, (effect) => void (effect.target = undefined))}
                    >
                      Re-aim
                    </button>
                  </div>
                ) : (
                  <span className="text-[11px] leading-relaxed text-signal-warn">
                    Not aimed yet — drag on the preview to choose what it points at. Until then it draws nothing.
                  </span>
                )}
              </Field>
              {/* How it arrives and leaves.
                  Beside the timings because that is what they are, and because
                  an effect that snaps in reads as a glitch rather than as
                  emphasis. */}
              <div className="grid grid-cols-2 gap-2">
                <Slider
                  label="Ease in"
                  value={selectedFocus.easeIn}
                  min={0}
                  max={2}
                  step={0.1}
                  suffix="s"
                  onChange={(value) => patchFocus(selectedFocus.id, (effect) => void (effect.easeIn = value))}
                />
                <Slider
                  label="Ease out"
                  value={selectedFocus.easeOut}
                  min={0}
                  max={2}
                  step={0.1}
                  suffix="s"
                  onChange={(value) => patchFocus(selectedFocus.id, (effect) => void (effect.easeOut = value))}
                />
              </div>

              {/* Starts and ends, not start and hold. "When should it be
                    removed" is the question, and it is the one dragging the
                    right edge answers — so the numbers should ask it too
                    rather than making someone add a hold to a start. */}
              <div className="grid grid-cols-2 gap-2">
                <Field label="Starts">
                  <input
                    className="input tnum"
                    type="number"
                    step={0.1}
                    min={0}
                    value={selectedFocus.start.toFixed(1)}
                    onChange={(event) =>
                      moveFocus(selectedFocus.id, Number(event.target.value), selectedFocus.duration)
                    }
                  />
                </Field>
                <Field label="Ends">
                  <input
                    className="input tnum"
                    type="number"
                    step={0.1}
                    min={0}
                    value={(selectedFocus.start + selectedFocus.duration).toFixed(1)}
                    onChange={(event) =>
                      moveFocus(selectedFocus.id, selectedFocus.start, Number(event.target.value) - selectedFocus.start)
                    }
                  />
                </Field>
              </div>

              <button
                className="btn btn-secondary btn-sm self-start"
                title="Remove this effect. The action stays on the timeline."
                onClick={() => {
                  commit((draft) => {
                    draft.sections.body.focus = draft.sections.body.focus.filter(
                      (effect) => effect.id !== selectedFocus.id,
                    );
                    return draft;
                  });
                  onSelectFocus(null);
                }}
              >
                Delete effect
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

/** Memoised: nothing here depends on the playhead, but the editor re-renders with it. */
export const Inspector = React.memo(InspectorBase);
